/**
 * keywordSeo.js — Wire keyword-targets.json into page SEO metadata
 * 
 * Maps service template slugs to Tier 1 money keywords for title/meta/H1 generation.
 * Supports multilingual keywords (en, es, pt, ko, ja, fr).
 */

import keywordTargets from '../data/keywords/keyword-targets.json';

// Build lookup: target_template → best keyword per language
const keywordMap = {};

if (keywordTargets?.tier1_money_keywords) {
  keywordTargets.tier1_money_keywords.forEach((kw) => {
    const template = kw.target_template;
    const langs = kw.languages || ['en'];
    
    langs.forEach((lang) => {
      const key = `${template}:${lang}`;
      if (!keywordMap[key] || kw.vol > keywordMap[key].vol) {
        keywordMap[key] = kw;
      }
    });
  });
}

// Tier 2 high-volume keywords as fallback
const tier2Map = {};
if (keywordTargets?.tier2_high_volume) {
  keywordTargets.tier2_high_volume.forEach((kw) => {
    const template = kw.target_template;
    if (!tier2Map[template] || kw.vol > tier2Map[template].vol) {
      tier2Map[template] = kw;
    }
  });
}

// Tier 3 long-tail patterns
const tier3Patterns = keywordTargets?.tier3_long_tail_patterns || [];

/**
 * Get the best money keyword for a service+locale combo
 */
export function getMoneyKeyword(serviceSlug, locale = 'en') {
  const key = `${serviceSlug}:${locale}`;
  return keywordMap[key] || keywordMap[`${serviceSlug}:en`] || null;
}

/**
 * Get tier 2 keyword for a service (English only, high volume)
 */
export function getTier2Keyword(serviceSlug) {
  return tier2Map[serviceSlug] || null;
}

/**
 * Generate SEO-optimized page title
 * Format: "Money Keyword in City, ST | Brand Name"
 */
export function generatePageTitle(serviceSlug, city, state, brandName, locale = 'en') {
  const kw = getMoneyKeyword(serviceSlug, locale);
  const tier2 = getTier2Keyword(serviceSlug);
  
  const keywordText = kw?.keyword || tier2?.keyword || serviceSlug.replace(/-/g, ' ');
  const capitalizedKeyword = keywordText.replace(/\b\w/g, (c) => c.toUpperCase());
  
  return `${capitalizedKeyword} in ${city}, ${state} | ${brandName}`;
}

/**
 * Generate SEO meta description with money keyword
 */
export function generateMetaDescription(serviceSlug, city, state, brandName, phone, locale = 'en') {
  const kw = getMoneyKeyword(serviceSlug, locale);
  const tier2 = getTier2Keyword(serviceSlug);
  
  const keywordText = kw?.keyword || tier2?.keyword || serviceSlug.replace(/-/g, ' ');
  
  return `Expert ${keywordText} in ${city}, ${state}. ${brandName} offers comprehensive injury care and treatment. Free consultation. Call ${phone} today. Se habla español.`;
}

/**
 * Generate H1 with keyword + city
 */
export function generateH1(serviceSlug, city, state, locale = 'en') {
  const kw = getMoneyKeyword(serviceSlug, locale);
  const tier2 = getTier2Keyword(serviceSlug);
  
  const keywordText = kw?.keyword || tier2?.keyword || serviceSlug.replace(/-/g, ' ');
  const capitalizedKeyword = keywordText.replace(/\b\w/g, (c) => c.toUpperCase());
  
  return `${capitalizedKeyword} in ${city}, ${state}`;
}

/**
 * Generate long-tail keywords for a city (for content body)
 */
export function generateLongTailKeywords(city, state, serviceSlug) {
  return tier3Patterns
    .filter((p) => p.includes('{city}'))
    .map((p) =>
      p
        .replace(/{city}/g, city)
        .replace(/{state}/g, state)
        .replace(/{body_part}/g, serviceSlug.replace(/-treatment|-doctor|-care/g, ''))
    )
    .slice(0, 10);
}

/**
 * Generate Schema.org structured data for a service page
 */
export function generateSchemaOrg(service, location, brand) {
  const kw = getMoneyKeyword(service.slug, 'en');
  
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name: brand.name,
    description: generateMetaDescription(service.slug, location.city, location.state, brand.name, brand.phone),
    url: `https://${brand.domain}/${location.slug}/${service.slug}`,
    telephone: brand.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: location.address,
      addressLocality: location.city,
      addressRegion: location.state,
      postalCode: location.zip,
      addressCountry: 'US',
    },
    medicalSpecialty: 'Orthopedic',
    availableService: {
      '@type': 'MedicalProcedure',
      name: service.display,
      description: `${service.display} services in ${location.city}, ${location.state}`,
    },
    ...(kw && {
      keywords: kw.keyword,
    }),
  };
}

export default {
  getMoneyKeyword,
  getTier2Keyword,
  generatePageTitle,
  generateMetaDescription,
  generateH1,
  generateLongTailKeywords,
  generateSchemaOrg,
};
